import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Radio, Button, Space, Typography, Tag } from 'antd';
import {
  FilterOutlined,
  ClearOutlined,
  UserOutlined,
  MedicineBoxOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import type {
  AudienceFilter,
  StatusFilter,
  RouterFilter,
} from '@/stores/AgentRegistryStore';
import { trackEvent } from '@/lib/telemetry';
import { listAgentCategories, type AgentCategoryRow } from '@/api/lookups';
import { AgentCategoryPickerDrawer } from './AgentCategoryPickerDrawer';
import { AgentStatusToggle } from '@/components/agentStatus';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
}

// Filter drawer for the agents list. Slides in from the right, applies filters
// instantly via MobX (no Apply button — feels faster), with a Clear all button
// at the bottom and a contextual count summary at the top.

export const AgentFilterDrawer = observer(function AgentFilterDrawer({ open, onClose }: Props) {
  const { agentRegistryStore: agents, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  // Counted by the server for the current search and filters.
  const matching = agents.totalCount;
  const activeCount = agents.activeFilterCount;

  // Categories are loaded lazily — only when the drawer first opens.
  // The lookup table is small (~20 rows) so this is one cheap fetch and
  // we don't bother caching across drawer open/close cycles. Refetching
  // keeps the list fresh in case the user just added a new category
  // from the AgentDetailPage's inline-add flow.
  const [categories, setCategories] = useState<AgentCategoryRow[]>([]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listAgentCategories()
      .then((rows) => { if (!cancelled) setCategories(rows); })
      .catch(() => { /* soft-fail — read-mode label falls back to "Category #N" */ });
    return () => { cancelled = true; };
  }, [open]);

  // Drawer-on-drawer: clicking the Category row opens the picker drawer
  // on top of this Filter drawer. antd handles the stacking + mask order
  // natively (the picker drawer renders with its own mask above the
  // Filter drawer's mask). On select, we set the store filter and close
  // the picker; the Filter drawer stays open so the operator can keep
  // tweaking.
  const [pickerOpen, setPickerOpen] = useState(false);
  const activeCategory =
    typeof agents.categoryFilter === 'number'
      ? categories.find((c) => c.id === agents.categoryFilter) ?? null
      : null;

  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const sectionBg = isDark ? colors.slate[900] : colors.slate[50];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      // Wide enough to render the body in two columns (Audience+Status
      // on the left, Skill router+Category on the right) so a typical
      // 800-pixel-tall viewport can show every option at once — no
      // scrolling required. The 2-column body kicks in at this width.
      width={760}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px 14px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: headerBg,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FilterOutlined style={{ fontSize: 17, color: colors.ink[600] }} />
            <Text strong style={{ fontSize: 16 }}>Filter agents</Text>
          </div>
          <Button
            size="small"
            type="text"
            icon={<ClearOutlined />}
            disabled={activeCount === 0}
            onClick={() => {
              agents.clearFilters();
              trackEvent('AgentFilters.Cleared');
            }}
          >
            Clear
          </Button>
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 12.5,
            color: isDark ? colors.slate[400] : colors.slate[500],
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {agents.isLoading ? (
            <span>Counting…</span>
          ) : activeCount === 0 ? (
            <span>Showing all {matching} agents.</span>
          ) : (
            <span>
              <strong style={{ color: isDark ? colors.slate[100] : colors.slate[900] }}>
                {matching}
              </strong>{' '}
              match · {activeCount} filter{activeCount === 1 ? '' : 's'} active
            </span>
          )}
        </div>
      </div>

      {/* Body — two columns so all four sections render side-by-side
          and fit in a single viewport. Falls back to one column on
          narrow screens via simple media-style flex wrapping. */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px 0 0',
          background: sectionBg,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          alignContent: 'start',
        }}
      >
        {/* AUDIENCE */}
        <FilterSection icon={<UserOutlined />} label="Audience" hint="Who can talk to this agent">
          <Radio.Group
            value={agents.audienceFilter}
            onChange={(e) => {
              agents.setAudienceFilter(e.target.value as AudienceFilter);
              trackEvent('AgentFilters.AudienceChanged', { value: e.target.value });
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            <Radio value={'all' satisfies AudienceFilter}>All</Radio>
            <Radio value={'patient' satisfies AudienceFilter}>Patient facing</Radio>
            <Radio value={'careTeam' satisfies AudienceFilter}>Provider facing</Radio>
          </Radio.Group>
        </FilterSection>

        {/* STATUS */}
        <FilterSection icon={<MedicineBoxOutlined />} label="Status" hint="Active = approved for production">
          <AgentStatusToggle
            value={agents.statusFilter}
            onChange={(v: StatusFilter) => {
              agents.setStatusFilter(v);
              trackEvent('AgentFilters.StatusChanged', { value: v });
            }}
          />
        </FilterSection>

        {/* ROUTER */}
        <FilterSection
          icon={<GlobalOutlined />}
          label="Skill router"
          hint="Pre-loads relevant skills per turn"
        >
          <Radio.Group
            value={agents.routerFilter}
            onChange={(e) => {
              agents.setRouterFilter(e.target.value as RouterFilter);
              trackEvent('AgentFilters.RouterChanged', { value: e.target.value });
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            <Radio value={'all' satisfies RouterFilter}>Any</Radio>
            <Radio value={'enabled' satisfies RouterFilter}>Router enabled</Radio>
            <Radio value={'disabled' satisfies RouterFilter}>Router disabled</Radio>
          </Radio.Group>
        </FilterSection>

        {/* CATEGORY — Button that opens a drawer with search. Direct
            consequence of the operator feedback ("Make the agent category
            selection happen in a drawer with search option"): the prior
            inline Select had cramped popup search and didn't scale once
            the lookup grew past ~15 rows. The new picker is also reused
            on the Identity card editor. */}
        <FilterSection
          icon={<AppstoreOutlined />}
          label="Category"
          hint="Domain bucket from ai.AiAgentCategory"
        >
          <Button
            block
            onClick={() => setPickerOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: 'auto',
              padding: '8px 12px',
              textAlign: 'left',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {activeCategory ? (
                <Tag
                  color={activeCategory.color || 'blue'}
                  icon={<AppstoreOutlined />}
                  style={{ margin: 0 }}
                >
                  {activeCategory.name}
                </Tag>
              ) : agents.categoryFilter === 'all' ? (
                <Text type="secondary">Any category</Text>
              ) : (
                <Tag color="default" style={{ margin: 0 }}>
                  Category #{agents.categoryFilter}
                </Tag>
              )}
            </span>
            <RightOutlined style={{ fontSize: 11, color: colors.slate[400] }} />
          </Button>
        </FilterSection>
      </div>

      <AgentCategoryPickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentCategoryId={
          typeof agents.categoryFilter === 'number' ? agents.categoryFilter : undefined
        }
        // Operators can add a category from the filter, but creating one must
        // NOT apply it: a brand-new category has no agents, so auto-filtering
        // by it would blank the list and read as a broken screen.
        assignOnCreate={false}
        clearLabel="Any category"
        onSelect={(c) => {
          // null = "Any category" was picked. Anything else commits the
          // numeric id as the filter value.
          if (c == null) {
            agents.setCategoryFilter('all');
            trackEvent('AgentFilters.CategoryChanged', { value: 'all' });
          } else {
            agents.setCategoryFilter(c.id);
            trackEvent('AgentFilters.CategoryChanged', { value: c.id });
          }
          setPickerOpen(false);
        }}
      />

      {/* Footer */}
      <div
        style={{
          padding: '12px 20px',
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: headerBg,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Filters apply instantly. Esc to close.
        </Text>
        <Space>
          <Button onClick={onClose}>Done</Button>
        </Space>
      </div>
    </Drawer>
  );
});

function FilterSection({
  icon,
  label,
  hint,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ padding: '14px 22px 18px', borderBottom: `1px dashed ${colors.slate[200]}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ color: colors.ink[600], fontSize: 13 }}>{icon}</span>
        <Text strong style={{ fontSize: 13 }}>{label}</Text>
      </div>
      {hint && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
          {hint}
        </Text>
      )}
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}
